import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-dsgov';
import styles from './balneario.module.scss';
import { BrButton, BrInput, BrLoading, BrSelect } from '@govbr-ds/react-components';
import { PaginasOutrosRegimes } from '../../../../../../../../../interfaces/IDadosOutrosRegimes';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import { useQuery } from '@tanstack/react-query';
import { ICodigoNcm } from '../../../../../../../../../interfaces/ICodigoNcm';
import { formatCurrency, removeFormatCurrency } from '../../../../../../../../../utils/formatCurrency';
import { DiefCfemContext } from '../../../../../../../../../contexts/DiefCfemContext';
import { useNotificacao } from '../../../../../../../../../hooks/useNotificacao';
import {
  IBalneario,
  IBalnearioInformacoesComBanhoEnviar,
  IBalnearioInformacoesSemBanhoEnviar,
} from '../../../../../../../../../interfaces/IBalneario';
import { validarCamposObrigatoriosComBanho, validarCamposObrigatoriosSemBanho } from './balnearioValidacoes';
import ErrorMessage from '../../../../../../../../../components/errorMessage/errorMessage';
import { ITributos } from '../../../../../../../../../interfaces/ITributos';
import AtualizarInformacoes from '../../../../../../atualizarInformacoes/atualizarInformacoes';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import SecaoDeducoes from 'components/secaoDeducoes/secaoDeducoes';

interface IBalnearioProps {
  isEdicao: boolean;
  setIsEdicao: React.Dispatch<React.SetStateAction<boolean>>;
  comBanho: boolean;
  setStepFormulario: React.Dispatch<React.SetStateAction<number>>;
  setBaseDeCalculo: React.Dispatch<
    // eslint-disable-next-line no-undef
    React.SetStateAction<PaginasOutrosRegimes>
  >;
}

const Balneario = ({ isEdicao, setIsEdicao, comBanho, setStepFormulario, setBaseDeCalculo }: IBalnearioProps) => {
  const VALOR_MINIMO = 0.01;
  const VALOR_MAXIMO = 1e10; //10 Digitos

  const [listaCodigosNCM, setListaCodigosNCM] = useState<ICodigoNcm[]>([]);
  const [tributosAdicionados, setTributosAdicionados] = useState<
    {
      nome: string;
      valor: number;
    }[]
  >([]);

  const [isCalculoInvalido, setIsCalculoInvalido] = useState(false);
  const [hasPrecoBanhoEspecificado, setHasPrecoBanhoEspecificado] = useState(comBanho);
  const [codigoNCM, setCodigoNCM] = useState('');

  const [receitaBrutaEstabelecimento, setReceitaBrutaEstabelecimento] = useState('');

  const [valorBanho, setValorBanho] = useState('');
  const [quantidade, setQuantidade] = useState('');

  const [infoSemBanho, setInfoSemBanho] = useState([{ label: 'Base de Cálculo(R$)', value: 0 }]);
  const [infoComBanho, setInfoComBanho] = useState([{ label: 'Base de Cálculo(R$)', value: 0 }]);

  const [isLoadingInfo, setIsLoadingInfo] = useState(false);

  const { getBalnearioNCMs, getBalneario, getTributosPossiveis } = useBackEndRequests();

  const context = useContext(DiefCfemContext);
  const idOperacao = context?.idOperacaoEditar ?? '';
  const idBalneario = context?.idBalneario ?? -1;

  const {
    data: balnearioNCMs,
    isLoading: NCMLoading,
    error: NCMError,
  } = useQuery<ICodigoNcm[], IErrorWithSentryId>({
    queryKey: ['balnearioNCMs'],
    queryFn: () => getBalnearioNCMs(),
    refetchOnWindowFocus: false,
  });

  const {
    data: balnearioData,
    isLoading: balnearioDataLoading,
    error: balnearioDataError,
  } = useQuery<IBalneario, IErrorWithSentryId>({
    queryKey: ['getBalneario', comBanho, idBalneario],
    queryFn: () => getBalneario(comBanho, idBalneario),
    refetchOnWindowFocus: false,
    enabled: isEdicao && idBalneario !== -1,
  });

  const {
    data: tributosData,
    isLoading: tributosLoading,
    error: tributosError,
  } = useQuery<ITributos, IErrorWithSentryId>({
    queryKey: ['getTributosPossiveis'],
    queryFn: () => getTributosPossiveis(idOperacao),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (balnearioNCMs) setListaCodigosNCM(balnearioNCMs);
  }, [balnearioNCMs]);

  useEffect(() => {
    if (balnearioData && isEdicao && balnearioNCMs) {
      if (comBanho) {
        setCodigoNCM(balnearioData?.codigoNcm || '');
        setValorBanho(formatCurrency(balnearioData?.valorBanho ?? 0));
        setQuantidade(balnearioData?.qtdeBanho?.toString() ?? '');
      } else {
        setCodigoNCM(balnearioData?.codigoNcm || '');
        setReceitaBrutaEstabelecimento(formatCurrency(balnearioData?.valorReceitaBruta ?? 0));
        setTributosAdicionados(balnearioData?.tributosValores || []);
      }
    }
  }, [balnearioData, comBanho, isEdicao, balnearioNCMs]);

  useEffect(() => {
    setIsCalculoInvalido(invalidarCalculo());
  }, [hasPrecoBanhoEspecificado, valorBanho, quantidade, receitaBrutaEstabelecimento]);

  const tributosIncidentes = formatCurrency(tributosAdicionados.reduce((soma, tributo) => soma + tributo.valor, 0));

  const invalidarCampoFinanceiro = (valor: string) => {
    if (valor === '') {
      return false;
    }

    const valorSemMascara = removeFormatCurrency(valor);

    return valorSemMascara < VALOR_MINIMO || valorSemMascara >= VALOR_MAXIMO;
  };

  const invalidarCampoNumero = (valor: string) => {
    if (valor === '') {
      return false;
    }

    const valorSemMascara = parseFloat(valor);

    return valorSemMascara < VALOR_MINIMO || valorSemMascara >= VALOR_MAXIMO;
  };

  const invalidarCalculo = () => {
    if (hasPrecoBanhoEspecificado) {
      const isValorBanhoInvalido = invalidarCampoFinanceiro(valorBanho);
      const isQuantidadeInvalida = invalidarCampoNumero(quantidade);

      return isValorBanhoInvalido || isQuantidadeInvalida;
    } else {
      const isReceitaBrutaInvalida = invalidarCampoFinanceiro(receitaBrutaEstabelecimento);

      return isReceitaBrutaInvalida;
    }
  };

  const receitaLiquidaEstabelecimento = formatCurrency(
    removeFormatCurrency(receitaBrutaEstabelecimento) - removeFormatCurrency(tributosIncidentes)
  );

  const {
    postSalvarBalnearioComBanho,
    postSalvarBalnearioSemBanho,
    putAtualizarBalnearioComBanho,
    putAtualizarBalnearioSemBanho,
    postReceberInformacaoBalnearioSemBanho,
    postReceberInformacaoBalnearioComBanho,
  } = useBackEndRequests();

  const mostrarNotificacao = useNotificacao();

  const salvarBaseCalculo = async () => {
    if (codigoNCM === '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Selecione um código NCM',
      });
      return false;
    }

    if (isCalculoInvalido) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Por favor, verifique os valores fornecidos e tente novamente.',
      });
      return false;
    }

    if (hasPrecoBanhoEspecificado) {
      if (validarCamposObrigatoriosComBanho(valorBanho, quantidade, mostrarNotificacao) === false) return;
      const dadosComBanho = {
        id: idBalneario !== -1 ? idBalneario : undefined,
        declaracaoItemId: parseInt(idOperacao),
        codigoNcm: codigoNCM,
        valorBanho: removeFormatCurrency(valorBanho),
        qtdeBanho: parseFloat(quantidade),
        houveBanho: hasPrecoBanhoEspecificado,
      };

      if (!isEdicao) {
        await postSalvarBalnearioComBanho(dadosComBanho)
          .then(() => {
            setIsEdicao(false);
            setStepFormulario(2);
            setBaseDeCalculo('lista');
          })
          .catch((err) =>
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao salvar dados do Balneário. Por favor, tente novamente.',
              ...(err && { complementoSentryId: err }),
            })
          );
      } else {
        await putAtualizarBalnearioComBanho(dadosComBanho)
          .then(() => {
            setIsEdicao(false);
            setStepFormulario(2);
            setBaseDeCalculo('lista');
          })
          .catch((err) =>
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao salvar dados do Balneário. Por favor, tente novamente.',
              ...(err && { complementoSentryId: err }),
            })
          );
      }
    } else {
      if (
        validarCamposObrigatoriosSemBanho(
          receitaBrutaEstabelecimento,
          tributosAdicionados,
          tributosData,
          mostrarNotificacao
        ) === false
      )
        return;
      if (removeFormatCurrency(receitaLiquidaEstabelecimento) < 0) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Receita líquida do Estabelecimento não pode ser negativa.',
        });
        return false;
      }
      const dadosSemBanho = {
        id: idBalneario !== -1 ? idBalneario : undefined,
        declaracaoItemId: parseInt(idOperacao),
        codigoNcm: codigoNCM,
        valorReceitaBruta: removeFormatCurrency(receitaBrutaEstabelecimento),
        houveBanho: hasPrecoBanhoEspecificado,
        tributosValores: tributosAdicionados,
      };
      if (!isEdicao) {
        await postSalvarBalnearioSemBanho(dadosSemBanho)
          .then(() => {
            setIsEdicao(false);
            setStepFormulario(2);
            setBaseDeCalculo('lista');
          })
          .catch((err) =>
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao salvar dados do Balneário. Por favor, tente novamente.',
              ...(err && { complementoSentryId: err }),
            })
          );
      } else {
        await putAtualizarBalnearioSemBanho(dadosSemBanho)
          .then(() => {
            setIsEdicao(false);
            setStepFormulario(2);
            setBaseDeCalculo('lista');
          })
          .catch((err) =>
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: 'Erro ao salvar dados do Balneário. Por favor, tente novamente.',
              ...(err && { complementoSentryId: err }),
            })
          );
      }
    }
  };

  const receberInformacaoBalnearioSemBanho = async () => {
    if (isCalculoInvalido) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Por favor, verifique os valores fornecidos e tente novamente.',
      });

      return;
    }

    if (
      !validarCamposObrigatoriosSemBanho(
        receitaBrutaEstabelecimento,
        tributosAdicionados,
        tributosData,
        mostrarNotificacao
      )
    ) {
      return;
    }

    if (removeFormatCurrency(receitaLiquidaEstabelecimento) < 0) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Receita líquida do Estabelecimento não pode ser negativa.',
      });
      return;
    }
    setIsLoadingInfo(true);
    const body: IBalnearioInformacoesSemBanhoEnviar = {
      valorReceitaBruta: removeFormatCurrency(receitaBrutaEstabelecimento),
      tributosValores: tributosAdicionados,
    };
    await postReceberInformacaoBalnearioSemBanho(body)
      .then((res) => {
        setInfoSemBanho([
          { label: 'Base de Cálculo(R$)', value: res.valorBaseCalculo },
          { label: 'Deduções (R$)', value: res.valorTributosIncidentes },
        ]);
        setIsLoadingInfo(false);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });
        setIsLoadingInfo(false);
      });
  };

  const receberInformacaoBalnearioComBanho = async () => {
    if (isCalculoInvalido) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Por favor, verifique os valores fornecidos e tente novamente.',
      });

      return;
    }

    if (!validarCamposObrigatoriosComBanho(valorBanho, quantidade, mostrarNotificacao)) {
      return;
    }

    setIsLoadingInfo(true);
    const body: IBalnearioInformacoesComBanhoEnviar = {
      valorTotalBanho: removeFormatCurrency(valorBanho),
      quantidade: parseFloat(quantidade),
    };
    await postReceberInformacaoBalnearioComBanho(body)
      .then((res) => {
        setInfoComBanho([{ label: 'Base de Cálculo(R$)', value: res.valorBaseCalculo }]);
        setIsLoadingInfo(false);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.',
          ...(err && { complementoSentryId: err }),
        });

        setIsLoadingInfo(false);
      });
  };

  return (
    <div className={styles.container}>
      {NCMError || balnearioDataError || tributosError ? (
        <ErrorMessage
          errorMessage={NCMError?.message ?? balnearioDataError?.message ?? ''}
          error={NCMError || balnearioDataError}
          onClickFunction={() => {
            setStepFormulario(2);
            setIsEdicao(false);
            setBaseDeCalculo('lista');
          }}
        />
      ) : (
        <>
          {balnearioDataLoading && isEdicao ? (
            <div className={styles.loadingContainer}>
              <BrLoading large />
            </div>
          ) : (
            <>
              <div className={styles.titleContainer}>
                <span className={styles.title}>Operação - Balneário</span>
              </div>
              <Row>
                <Col>
                  <div className={styles.label}>Código NCM</div>
                  {NCMLoading ? (
                    <BrLoading />
                  ) : (
                    <BrSelect
                      className={styles.select}
                      placeholder='Selecione...'
                      value={codigoNCM}
                      onChange={(value: string) => {
                        setCodigoNCM(value);
                      }}
                      options={listaCodigosNCM?.map((item: ICodigoNcm) => ({
                        label: `${item.codigo} - ${item.descricao}`,
                        value: item.codigo,
                      }))}
                    />
                  )}
                </Col>
                <Col className={styles.switchContainer}>
                  <span className={styles.label}>Há Preço de Banho Especificado?</span>
                  {isEdicao ? (
                    <label className={styles.text}>{hasPrecoBanhoEspecificado ? 'Sim' : 'Não'}</label>
                  ) : (
                    <div
                      className={`br-switch icon ${styles.switch}`}
                      role='switch'
                      aria-checked={hasPrecoBanhoEspecificado}
                      tabIndex={0}
                      onClick={() => setHasPrecoBanhoEspecificado(!hasPrecoBanhoEspecificado)}
                      onKeyDown={() => setHasPrecoBanhoEspecificado(!hasPrecoBanhoEspecificado)}
                    >
                      <input
                        id='switch-icon'
                        type='checkbox'
                        name='switch-icon'
                        disabled={true}
                        checked={hasPrecoBanhoEspecificado}
                        role='switch'
                      />
                      <label className={styles.text}>{hasPrecoBanhoEspecificado ? 'Sim' : 'Não'}</label>
                    </div>
                  )}
                </Col>
              </Row>
              <div className={styles.metodoContainer}>
                <div className={styles.metodoTitleContainer}>
                  <span className={styles.metodoTitle}>
                    {hasPrecoBanhoEspecificado
                      ? 'Método com especificação do preço do banho'
                      : 'Método sem especificação do preço do banho'}
                  </span>
                </div>
                {hasPrecoBanhoEspecificado ? (
                  <div className={styles.metodoForm}>
                    <Row align-items-center>
                      <Col auto>
                        <div className={styles.label}>Valor Total do Banho (R$)</div>
                        <BrInput
                          autoComplete='off'
                          numeric={{
                            prefix: 'R$ ',
                            thousandSeparator: '.',
                            decimalScale: 2,
                            fixedDecimalScale: false,
                          }}
                          className={styles.input}
                          value={valorBanho}
                          onChange={(e) => setValorBanho(e.target.value)}
                        />
                      </Col>
                      <Col size={3} auto>
                        <div className={styles.label}>Quantidade</div>
                        <BrInput
                          className={styles.input}
                          autoComplete='off'
                          numeric={{
                            decimalScale: 2,
                            fixedDecimalScale: false,
                          }}
                          value={quantidade}
                          onChange={(e) => setQuantidade(e.target.value.replace(',', '.'))}
                        />
                      </Col>
                      <Col className={styles.baseCalculoContainer} auto>
                        <div className={styles.label}>Preço Médio (R$)</div>
                        <div className={styles.textBaseCalculo}>
                          <span className={styles.text}>
                            {valorBanho && quantidade && isCalculoInvalido === false
                              ? formatCurrency(removeFormatCurrency(valorBanho) / parseFloat(quantidade))
                              : '-'}
                          </span>
                        </div>
                      </Col>
                      <Col>
                        <AtualizarInformacoes
                          onClickFunction={() => receberInformacaoBalnearioComBanho()}
                          info={infoComBanho}
                          widthFitContent={false}
                          isLoading={isLoadingInfo}
                        />
                      </Col>
                    </Row>
                  </div>
                ) : (
                  <>
                    <div className={styles.metodoForm}>
                      <Row>
                        <Col>
                          <div className={styles.label}>Receita Bruta do Estabelecimento (R$)</div>
                          <BrInput
                            autoComplete='off'
                            numeric={{
                              prefix: 'R$ ',
                              thousandSeparator: '.',
                              decimalScale: 2,
                              fixedDecimalScale: false,
                            }}
                            className={styles.input}
                            value={receitaBrutaEstabelecimento}
                            onChange={(e) => setReceitaBrutaEstabelecimento(e.target.value)}
                          />
                        </Col>
                        <Col></Col>
                      </Row>
                    </div>
                    <SecaoDeducoes
                      key={codigoNCM}
                      deducoesPossiveis={tributosData}
                      deducoesAdicionadas={tributosAdicionados}
                      setDeducoesAdicionadas={setTributosAdicionados}
                      valorReceitaBruta={receitaBrutaEstabelecimento}
                    />
                    <div className={styles.infoContainer}>
                      <AtualizarInformacoes
                        onClickFunction={() => receberInformacaoBalnearioSemBanho()}
                        info={infoSemBanho}
                        widthFitContent={false}
                        isLoading={isLoadingInfo}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className={styles.containerButton}>
                <BrButton
                  secondary
                  onClick={() => {
                    setStepFormulario(2);
                    setIsEdicao(false);
                    setBaseDeCalculo('lista');
                  }}
                >
                  <span className={styles.button}>
                    <i className={`fas fa-arrow-left`} /> Voltar
                  </span>
                </BrButton>
                <BrButton
                  onClick={() => {
                    salvarBaseCalculo();
                  }}
                  primary
                >
                  <span className={styles.button}>
                    {isEdicao ? (
                      <>
                        <i className={`fas fa-save`} /> Salvar Base de Cálculo
                      </>
                    ) : (
                      <>
                        <i className={`fas fa-plus`} /> Adicionar Base de Cálculo
                      </>
                    )}
                  </span>
                </BrButton>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Balneario;
